/**
 * Event operations, Batch 2A (MKK_ADOPTION_BATCH_2.md): a staff-managed registration list per event, capacity and a
 * waiting list, and manual staff check-in. The rules only — no I/O. The API (apps/api/src/event-ops.js) reads the
 * rows, asks here, and writes inside ONE transaction, so a seat count is never stale when it is used.
 *
 * Staff only. Nothing here is shown to members or the public, and nothing is sent to anyone.
 *
 *   list   { state: 'open'|'closed'|'cancelled', capacity: null|1..10000, waitlist: boolean, version }
 *   entry  { id, displayName, partySize, status: 'registered'|'waitlisted'|'cancelled', note, waitlistedAt,
 *            checkedInAt, checkedInBy, cancelledAt, version }
 *   event  { status: 'draft'|'scheduled'|'published'|'cancelled'|'archived', startsAt, endsAt }
 *
 * `capacityOn`: the `event_capacity` capability. Off, a stored limit is kept but NOT applied (and new limits are
 * refused, not silently ignored), so switching it back on applies the same limit again.
 */
const T = require('./tasks');

const LIST_STATES = ['open', 'closed', 'cancelled'];
const STATUSES = ['registered', 'waitlisted', 'cancelled'];
const MAX_NAME = 120;
const MAX_NOTE = 500;
const MAX_PARTY = 20;
const MAX_CAPACITY = 10000;
const MAX_ENTRIES = 5000;            // per list, cancelled included: a list is not a membership register

const LIST_STATE_NO = { open: 'Åpen', closed: 'Stengt for nye', cancelled: 'Avlyst' };
const STATUS_NO = { registered: 'Påmeldt', waitlisted: 'Venteliste', cancelled: 'Avmeldt' };

const fail = (field, message, code = 'invalid') => ({ ok: false, code, errors: { [field]: message } });
const unknownKeys = (input, allowed) => Object.keys(input).filter((k) => !allowed.includes(k));

/** Whether anything may be changed on this event's list at all (reads are always allowed). */
function writable(event, list) {
  if (!event || event.status === 'draft') return { ok: false, code: 'not_found' };
  if (event.status === 'cancelled' || event.status === 'archived') return { ok: false, code: 'event_closed', message: 'Arrangementet er avlyst eller arkivert. Listen kan bare leses.' };
  if (list && list.state === 'cancelled') return { ok: false, code: 'list_cancelled', message: 'Påmeldingslisten er avlyst. Den kan bare leses.' };
  return { ok: true };
}

/** Seats and people, from the entries as they are now. */
function counts(entries) {
  const c = { registeredSeats: 0, registeredEntries: 0, waitlistedSeats: 0, waitlistedEntries: 0, cancelledEntries: 0,
              checkedInSeats: 0, checkedInEntries: 0, total: entries.length };
  for (const e of entries) {
    if (e.status === 'registered') {
      c.registeredSeats += e.partySize; c.registeredEntries++;
      if (e.checkedInAt) { c.checkedInSeats += e.partySize; c.checkedInEntries++; }
    } else if (e.status === 'waitlisted') { c.waitlistedSeats += e.partySize; c.waitlistedEntries++; }
    else c.cancelledEntries++;
  }
  return c;
}

/** The limit that applies now: null when there is none, or when the capacity module is off. */
const limitOf = (list, capacityOn) => (capacityOn && list && Number.isSafeInteger(list.capacity) ? list.capacity : null);
const waitlistOn = (list, capacityOn) => Boolean(capacityOn && list && list.waitlist && limitOf(list, capacityOn) !== null);

/**
 * The list's settings, new or changed. → { ok, value: { state, capacity, waitlist }, changed } | { ok:false, errors }
 * `current`: counts(entries) — a limit below the seats already taken, or switching the waiting list off with people
 * on it, is refused: nobody is moved or removed silently.
 */
function listInput(input, base, { capacityOn, current = counts([]) } = {}) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail('list', 'Ugyldige innstillinger.');
  const bad = unknownKeys(input, ['state', 'capacity', 'waitlist', 'version']);
  if (bad.length) return fail(bad[0], 'Ukjent felt.');
  const b = base || { state: 'open', capacity: null, waitlist: false };
  const out = { state: b.state, capacity: b.capacity === undefined ? null : b.capacity, waitlist: Boolean(b.waitlist) };
  if (input.state !== undefined) {
    if (!LIST_STATES.includes(input.state)) return fail('state', 'Velg åpen, stengt for nye eller avlyst.');
    if (base && base.state === 'cancelled' && input.state !== 'cancelled') return fail('state', 'En avlyst liste kan ikke åpnes igjen. Lag en ny ved behov.', 'list_cancelled');
    out.state = input.state;
  }
  for (const k of ['capacity', 'waitlist']) {
    if (input[k] !== undefined && !capacityOn) return fail(k, 'Kapasitet og venteliste er ikke slått på for moskeen.', 'capacity_off');
  }
  if (input.capacity !== undefined) {
    if (input.capacity === null) out.capacity = null;
    else if (!Number.isSafeInteger(input.capacity) || input.capacity < 1 || input.capacity > MAX_CAPACITY) return fail('capacity', `Kapasiteten må være 1–${MAX_CAPACITY}, eller tom for ingen grense.`);
    else out.capacity = input.capacity;
  }
  if (input.waitlist !== undefined) {
    if (typeof input.waitlist !== 'boolean') return fail('waitlist', 'Venteliste må være på eller av.');
    out.waitlist = input.waitlist;
  }
  if (out.capacity === null) out.waitlist = false;   // no limit: nobody waits
  if (capacityOn && out.capacity !== null && out.capacity < current.registeredSeats) {
    return fail('capacity', `${current.registeredSeats} plasser er allerede tatt. Meld av eller flytt noen først.`, 'below_taken');
  }
  if (!out.waitlist && current.waitlistedEntries > 0 && (input.waitlist === false || input.capacity === null)) {
    return fail('waitlist', `${current.waitlistedEntries} står på ventelisten. Flytt dem opp eller meld dem av først.`, 'waitlist_not_empty');
  }
  const changed = base ? ['state', 'capacity', 'waitlist'].filter((k) => (k === 'waitlist' ? Boolean(base[k]) !== out[k] : (base[k] ?? null) !== out[k])) : ['state', 'capacity', 'waitlist'];
  return { ok: true, value: out, changed };
}

/** A new entry's fields. → { ok, value: { displayName, partySize, note } } | { ok:false, errors } */
function entryInput(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail('entry', 'Ugyldig påmelding.');
  const bad = unknownKeys(input, ['displayName', 'partySize', 'note', 'checkIn', 'allowDuplicate']);
  if (bad.length) return fail(bad[0], 'Ukjent felt.');
  const name = T.oneLine(input.displayName, 'navnet', MAX_NAME);
  if (!name.ok) return fail('displayName', name.message);
  const size = input.partySize === undefined ? 1 : input.partySize;
  if (!Number.isSafeInteger(size) || size < 1 || size > MAX_PARTY) return fail('partySize', `Antall må være 1–${MAX_PARTY}.`);
  const note = T.text(input.note, 'notatet', MAX_NOTE);
  if (!note.ok) return fail('note', note.message);
  if (input.checkIn !== undefined && typeof input.checkIn !== 'boolean') return fail('checkIn', 'Ugyldig verdi.');
  if (input.allowDuplicate !== undefined && typeof input.allowDuplicate !== 'boolean') return fail('allowDuplicate', 'Ugyldig verdi.');
  return { ok: true, value: { displayName: name.value, partySize: size, note: note.value }, checkIn: input.checkIn === true,
    allowDuplicate: input.allowDuplicate === true };
}

/**
 * Active entries (registered or waiting) whose name is the same as `displayName`, case, accents and ø/æ folded.
 * A second phone call about the same person must not take a second seat; two people who share a name are added with
 * `allowDuplicate: true`, on purpose. A cancelled entry is not a duplicate: adding that person again is the way back.
 */
function duplicates(entries, displayName) {
  const want = fold(String(displayName || '').trim());
  if (!want) return [];
  return entries.filter((e) => e.status !== 'cancelled' && fold(e.displayName) === want);
}

/** A change to an entry: name, party size, note, or cancelling it. → { ok, value, changed } | { ok:false, errors } */
function entryChange(input, base) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail('entry', 'Ugyldig endring.');
  const bad = unknownKeys(input, ['displayName', 'partySize', 'note', 'status', 'version']);
  if (bad.length) return fail(bad[0], 'Ukjent felt.');
  const out = { displayName: base.displayName, partySize: base.partySize, note: base.note ?? null, status: base.status };
  if (input.displayName !== undefined) { const r = T.oneLine(input.displayName, 'navnet', MAX_NAME); if (!r.ok) return fail('displayName', r.message); out.displayName = r.value; }
  if (input.partySize !== undefined) {
    if (!Number.isSafeInteger(input.partySize) || input.partySize < 1 || input.partySize > MAX_PARTY) return fail('partySize', `Antall må være 1–${MAX_PARTY}.`);
    out.partySize = input.partySize;
  }
  if (input.note !== undefined) { const r = T.text(input.note, 'notatet', MAX_NOTE); if (!r.ok) return fail('note', r.message); out.note = r.value; }
  if (input.status !== undefined) {
    if (input.status !== base.status && input.status !== 'cancelled') return fail('status', 'En påmelding kan bare meldes av her. Legg den inn på nytt for å melde på igjen.');
    out.status = input.status;
  }
  if (base.status === 'cancelled' && (out.partySize !== base.partySize)) return fail('partySize', 'En avmeldt påmelding kan ikke endres.', 'cancelled');
  if (out.status === 'cancelled' && base.status !== 'cancelled' && base.checkedInAt) {
    return fail('status', 'Personen er sjekket inn. Angre innsjekkingen først.', 'checked_in');
  }
  const changed = ['displayName', 'partySize', 'note', 'status'].filter((k) => (out[k] ?? null) !== (base[k] ?? null));
  return { ok: true, value: out, changed };
}

/**
 * Where a party goes: 'registered' when it fits (or there is no limit), 'waitlisted' when it does not and the
 * waiting list is on; otherwise refused as full. `entries`: every entry of the list as it is now.
 */
function place(list, entries, partySize, capacityOn) {
  const limit = limitOf(list, capacityOn);
  if (limit === null) return { ok: true, status: 'registered' };
  const free = limit - counts(entries).registeredSeats;
  if (partySize <= free) return { ok: true, status: 'registered' };
  if (waitlistOn(list, capacityOn)) return { ok: true, status: 'waitlisted' };
  return { ok: false, code: 'full', message: free > 0 ? `Bare ${free} ${free === 1 ? 'plass' : 'plasser'} igjen.` : 'Listen er full.' };
}

/** A registered party grown: it must still fit (an increase is never split onto the waiting list). */
function resizeFits(list, entries, entry, newSize, capacityOn) {
  if (entry.status !== 'registered' || newSize <= entry.partySize) return { ok: true };
  const limit = limitOf(list, capacityOn);
  if (limit === null) return { ok: true };
  const others = counts(entries.filter((e) => e.id !== entry.id)).registeredSeats;
  if (others + newSize <= limit) return { ok: true };
  return { ok: false, code: 'full', message: `Det er bare ${Math.max(0, limit - others)} plasser til denne påmeldingen.` };
}

/** The waiting list in order: first waitlisted first; the id settles a tie, so the order is total. */
function queue(entries) {
  return entries.filter((e) => e.status === 'waitlisted')
    .sort((a, b) => String(a.waitlistedAt || '').localeCompare(String(b.waitlistedAt || '')) || String(a.id).localeCompare(String(b.id)));
}

/**
 * Moving a waiting party up. On purpose, by staff (nothing is sent, so the person is told by the mosque): the party
 * must fit. The panel offers the next in line; another may be chosen, and the audit says so (`reg_out_of_turn`).
 */
function promote(list, entries, entry, capacityOn) {
  if (!capacityOn) return { ok: false, code: 'capacity_off', message: 'Kapasitet og venteliste er ikke slått på for moskeen.' };
  if (!entry || entry.status !== 'waitlisted') return { ok: false, code: 'not_waitlisted', message: 'Påmeldingen står ikke på ventelisten.' };
  const limit = limitOf(list, capacityOn);
  const free = limit === null ? Infinity : limit - counts(entries).registeredSeats;
  if (entry.partySize > free) return { ok: false, code: 'full', message: free > 0 ? `Bare ${free} ${free === 1 ? 'plass' : 'plasser'} er ledig.` : 'Ingen plasser er ledige.' };
  const first = queue(entries)[0];
  return { ok: true, outOfTurn: Boolean(first && first.id !== entry.id) };
}

/**
 * Check in, or undo. Only a registered entry, on a list and event that still take changes. The same state again
 * changes nothing (`same`), so a double tap at the door writes nothing twice.
 */
function checkIn(event, list, entry, checkedIn) {
  const w = writable(event, list);
  if (!w.ok) return w;
  if (typeof checkedIn !== 'boolean') return { ok: false, code: 'invalid', message: 'Send { checkedIn: true } eller { checkedIn: false }.' };
  if (entry.status !== 'registered') {
    return { ok: false, code: 'not_registered', message: entry.status === 'waitlisted' ? 'Personen står på ventelisten. Flytt opp først.' : 'Påmeldingen er avmeldt.' };
  }
  if (Boolean(entry.checkedInAt) === checkedIn) return { ok: true, same: true };
  return { ok: true, same: false };
}

/** Whether the event has ended (for "did not come"): its end, or its start when it has none. */
function ended(event, nowIso) {
  const end = event.endsAt || event.startsAt;
  return Boolean(end) && end <= nowIso;
}

/** Counts only — never a name. `capacityOn` decides whether a limit and the remaining seats are stated. */
function summary(event, list, entries, { capacityOn, now }) {
  const c = counts(entries);
  const limit = limitOf(list, capacityOn);
  return {
    listState: list ? list.state : null,
    capacity: limit, remaining: limit === null ? null : Math.max(0, limit - c.registeredSeats),
    waitlist: waitlistOn(list, capacityOn),
    storedLimitInactive: Boolean(list && !capacityOn && Number.isSafeInteger(list.capacity)),
    registeredSeats: c.registeredSeats, registeredEntries: c.registeredEntries,
    waitlistedSeats: c.waitlistedSeats, waitlistedEntries: c.waitlistedEntries,
    cancelledEntries: c.cancelledEntries,
    checkedInSeats: c.checkedInSeats, checkedInEntries: c.checkedInEntries,
    // Shown only once the event is over: before that, "not checked in" just means "not here yet".
    notArrivedEntries: ended(event, now) ? c.registeredEntries - c.checkedInEntries : null,
  };
}

/** The aggregate over several lists' summaries (the event list's `totals`): counts only, never a name. */
function totals(summaries) {
  const t = { events: summaries.length, lists: 0, registeredSeats: 0, registeredEntries: 0, waitlistedSeats: 0, waitlistedEntries: 0,
              checkedInSeats: 0, checkedInEntries: 0, cancelledEntries: 0 };
  for (const s of summaries) {
    if (!s) continue;
    t.lists++;
    for (const k of ['registeredSeats', 'registeredEntries', 'waitlistedSeats', 'waitlistedEntries', 'checkedInSeats', 'checkedInEntries', 'cancelledEntries']) t[k] += s[k] || 0;
  }
  return t;
}

const VIEWS = ['active', 'registered', 'waitlisted', 'cancelled', 'all'];
const ARRIVED = ['any', 'yes', 'no'];
// Search folds case and accents, and ø/æ (which Unicode does not decompose): «oyvind» finds «Øyvind», «aerlig» «Ærlig».
const fold = (s) => String(s || '').toLocaleLowerCase('nb').replace(/ø/g, 'o').replace(/æ/g, 'ae').normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

/** The list's filter, from a query string. → { ok, value: { view, arrived, q } } | { ok:false, message } */
function filter(q = {}) {
  const view = q.status || 'active', arrived = q.arrived || 'any';
  if (!VIEWS.includes(view)) return { ok: false, message: 'Ugyldig statusfilter.' };
  if (!ARRIVED.includes(arrived)) return { ok: false, message: 'Ugyldig filter for oppmøte.' };
  const text = q.q === undefined || q.q === null ? '' : String(q.q);
  if (text.length > 120) return { ok: false, message: 'Søket er for langt.' };
  return { ok: true, value: { view, arrived, q: fold(text.trim()) } };
}

const RANK = { registered: 0, waitlisted: 1, cancelled: 2 };
/** Filtered and in one order: registered by name, then the waiting list in queue order, then cancelled by name. */
function view(entries, f) {
  const pos = new Map(queue(entries).map((e, i) => [e.id, i]));
  return entries
    .filter((e) => (f.view === 'all' ? true : f.view === 'active' ? e.status !== 'cancelled' : e.status === f.view))
    .filter((e) => (f.arrived === 'any' ? true : f.arrived === 'yes' ? Boolean(e.checkedInAt) : e.status === 'registered' && !e.checkedInAt))
    .filter((e) => !f.q || fold(e.displayName).includes(f.q))
    .sort((a, b) => RANK[a.status] - RANK[b.status]
      || (a.status === 'waitlisted' ? pos.get(a.id) - pos.get(b.id) : 0)
      || String(a.displayName).localeCompare(String(b.displayName), 'nb') || String(a.id).localeCompare(String(b.id)));
}

/** Audit fields (packages/core/audit.js EVENT_OPS_FIELDS): operational only — never the name or the note. */
const listAudit = (l) => ({ reg_list_state: l.state, reg_capacity: l.capacity ?? null, reg_waitlist: Boolean(l.waitlist) });
const entryAudit = (e) => ({ reg_status: e.status, reg_party_size: e.partySize, reg_checked_in: Boolean(e.checkedInAt) });
/** Column names of what changed (never values), with the operational ones translated to their audit names. */
const changedFields = (keys) => keys.map((k) => ({ displayName: 'display_name', partySize: 'party_size', note: 'note' }[k] || k));

module.exports = {
  LIST_STATES, STATUSES, LIST_STATE_NO, STATUS_NO, MAX_NAME, MAX_NOTE, MAX_PARTY, MAX_CAPACITY, MAX_ENTRIES, VIEWS, ARRIVED,
  writable, counts, limitOf, waitlistOn, listInput, entryInput, entryChange, place, resizeFits, queue, promote, checkIn,
  ended, summary, totals, duplicates, filter, view, listAudit, entryAudit, changedFields,
};
